import { useState } from 'react';
import UserImportButton from '../common/Button';
import DashboardTabs, { type DashboardTab } from './DashboardTabs';
import Modal from '../common/Modal';
import {
  type UserImportTemplate,
  type UserImportSflag,
  UserImportStats,
  useImportUsersMutation,
} from '../hooks/useUsers';
import Navbar from './Navbar';
import UsersTab from './UsersTab';
import InvoicesTab from './InvoicesTab';
import AllTab from './AllTab';
import BounceEmailsTab from './BounceEmailsTab';

type DashboardProps = {
  onLogout: () => void;
};

function Dashboard({ onLogout }: DashboardProps) {
  const [activeTab, setActiveTab] = useState<DashboardTab>('all');
  const [importStats, setImportStats] = useState<UserImportStats | null>(null);
  const importUsersMutation = useImportUsersMutation();
  const isImporting = importUsersMutation.isPending;
  const importedUsers = importUsersMutation.data?.users;
  const importErrorMessage = (importUsersMutation.error as Error | null)?.message ?? '';

  const handleImportUsers = async (file: File, mode: UserImportSflag, template: UserImportTemplate | null) => {
    const result = await importUsersMutation.mutateAsync({ file, mode, template });
    setImportStats(result.stats);
  };

  return (
    <main className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-100 pb-10">
      <Navbar onLogout={onLogout} />
      <section className="mx-auto mt-10 flex w-full max-w-[96rem] flex-col gap-6 px-6">
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white/90 shadow-xl shadow-slate-200/60 backdrop-blur">
          <div className="flex flex-col gap-6 bg-indigo-600 p-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-3">
              <p
                className="inline-flex w-fit rounded-full border border-white/30 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white"
              >
                Operations Console
              </p>
              <h1 className="text-3xl font-bold tracking-tight text-white">Invoice Automation</h1>
              <p className="max-w-2xl text-sm text-indigo-100">
                Monitor users, invoices, and transactions from one workspace with fast search and sortable data tables.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <div className="col-span-2 sm:col-span-3 pt-1">
                <UserImportButton onImportConfirm={handleImportUsers} isLoading={isImporting} />
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
          <div className="flex flex-wrap items-center gap-2">
            <DashboardTabs activeTab={activeTab} onTabChange={setActiveTab} />
          </div>
        </div>

        {activeTab === 'users' && (
          <UsersTab importData={importedUsers} importErrorMessage={importErrorMessage} />
        )}
        {activeTab === 'invoices' && <InvoicesTab />}
        {activeTab === 'bounceEmails' && <BounceEmailsTab />}
        {activeTab === 'all' && <AllTab />}
      </section>

      <Modal isOpen={Boolean(importStats)} onClose={() => setImportStats(null)} title="User Import Summary">
        {importStats && (
          <div className="space-y-3">
            <p className="text-sm text-slate-700">User import finished successfully.</p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total Rows</p>
                <p className="mt-1 text-lg font-bold text-slate-900">{importStats.totalRows}</p>
              </div>
              <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-green-700">Imported</p>
                <p className="mt-1 text-lg font-bold text-green-800">{importStats.imported}</p>
              </div>
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Skipped</p>
                <p className="mt-1 text-lg font-bold text-amber-800">{importStats.skipped}</p>
              </div>
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-red-700">Failures</p>
                <p className="mt-1 text-lg font-bold text-red-800">{importStats.failures.length}</p>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </main>
  );
}

export default Dashboard;
